import {
  Grid,
  Stack,
} from '@mantine/core'
import { CurrentWaterUsageComponent } from '../Home/Dashboard/CurrentWaterUsageComponent'
import {
  useDevicesLoading,
  useVisibleDevices,
} from '@data/device/device'
import { HomeStats } from '@components/main/LocationPerformance/components/SummaryStats'
import { useMemo } from 'react'
import { useTicketsSummary } from '@data/tickets/tickets'
import {
  format,
  sub,
} from 'date-fns'
import { useActiveAlertsQuery } from '@data/alerts/alerts'
import { TicketSummary } from '@data/tickets/model/ticket'


export interface TicketSummaryExtended extends TicketSummary {
  deviceName: string
}

const Home = () => {
  const devices = useVisibleDevices()
  const isLoadingDevices = useDevicesLoading()
  const { data: alertData = [] } = useActiveAlertsQuery()

  const { data: ticketsData } = useTicketsSummary({
    startDate: format(new Date(2025, 11, 31, 0), 'yyyy-MM-dd\'T\'HH:mm:ssxxx'),
    endDate: format(new Date(), 'yyyy-MM-dd\'T\'23:59:00xxx'),
    status: ['open', 'in_progress'],
    issueType: [],
  })

  const { filteredTicketSummary, healthyDeviceCount } = useMemo(() => {
    if (!ticketsData?.length || !devices?.length) {
      return { filteredTicketSummary: [], healthyDeviceCount: devices?.length ?? 0 }
    }

    const deviceMap = new Map(
      devices
        .filter(d => d.dUuid)
        .map(d => [d.dUuid, d]),
    )

    const filteredTicketSummary = ticketsData
      .filter(ticket => deviceMap.has(ticket.dUuid))
      .map(ticket => ({
        ...ticket,
        deviceName: deviceMap.get(ticket.dUuid)?.deviceName ?? 'Unknown Device Name',
      }))

    const devicesWithIssues = new Set(filteredTicketSummary.map(t => t.dUuid))
    const healthyDeviceCount = devices.filter(d => !devicesWithIssues.has(d.dUuid)).length

    return { filteredTicketSummary, healthyDeviceCount }
  }, [ticketsData, devices])

  const filteredAlerts = useMemo(() => {
    if (!alertData?.length || !devices?.length) return []

    const visibleDUuids = new Set(
      devices
        .filter((device) => device.dUuid)
        .map((device) => device.dUuid),
    )

    return alertData.filter((alert) =>
      visibleDUuids.has(alert.dUuid),
    )
  }, [alertData, devices])

  const newAddedDevices = useMemo(() => {
    if (isLoadingDevices || !devices?.length) return []
    const today = new Date()
    const lastWeek = sub(today, { days: 7 })


    return devices.filter((device) => {
      const installDate = new Date(device.deviceSettings.installDate)
      return installDate >= lastWeek
    })
  }, [devices, isLoadingDevices])

  // if (isLoading || isFetching) {
  //   return <div>Loading...</div>
  // }

  return (
    <Stack gap="lg">
      <Grid>
        <Grid.Col span={{ base: 12 }}>
          <HomeStats
            healthyDevices={healthyDeviceCount}
            ticketsSummary={filteredTicketSummary}
            alerts={filteredAlerts}
            newAddedDevices={newAddedDevices}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12 }}>
          <CurrentWaterUsageComponent />
        </Grid.Col>
      </Grid>
    </Stack>
  )
}

export default Home
